/*
 * Convert SVG shapes into equivalent "path" shapes for use by PIXI JSON
 * @see simplify-svg-for-pixi.js for more information
 *
 * (This code was originally adapted from an existing SVGO plugin named convertShapeToPath in the SVGO plugins folder)
 *
 * NOTE: the object in this file depend on being in the format provided by calling XmlJs.xml2js(svg, { compact: false })
 * see https://github.com/JFXtras/jfxtras-labs/blob/2.2/src/main/java/jfxtras/labs/util/ShapeConverter.java
 */

import { propEq, has, map, path, mapObjIndexed, is } from 'ramda';
import { Ajv, SvgCommands } from '@locuslabs/json-schemas';

// ---------------------------------------------------------------------------------------------------------------------
//
// Validators
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * use Ajv to create a validation function for schema[typeName]
 * The resulting function signature will be :: POJO -> undefined|[String]
 */
const validators = {
    line: Ajv.createValidator(SvgCommands.SvgLineCommand, SvgCommands.StringPatterns),
    rect: Ajv.createValidator(SvgCommands.SvgRectCommand, SvgCommands.StringPatterns),
    circle: Ajv.createValidator(SvgCommands.SvgCircleCommand, SvgCommands.StringPatterns),
};

// ---------------------------------------------------------------------------------------------------------------------
//
// Predicates
//
// ---------------------------------------------------------------------------------------------------------------------
const isCircle = propEq('circle', 'elem');
const isComment = has('comment');
const isEllipse = propEq('ellipse', 'elem');
const isFeColorMatrix = propEq('feColorMatrix', 'elem');
const isFontFace = propEq('font-face', 'elem');
const isG = propEq('g', 'elem');
const isGlyph = propEq('glyph', 'elem');
const isImage = propEq('image', 'elem');
const isLine = propEq('line', 'elem');
const isMissingGlyph = propEq('missing-glyph', 'elem');
const isPath = propEq('path', 'elem');
const isPolygon = propEq('polygon', 'elem');
const isPolyline = propEq('polyline', 'elem');
const isRect = propEq('rect', 'elem');
const isStop = propEq('stop', 'elem');
const isText = has('text');
const isUse = propEq('use', 'elem');

const expectAttributes = (typeName, o) => {
    const attributes = map(v => v.value, o.attrs);

    const isNumberAttribute = name => path(['schema', 'properties', name, '$ref'], validator) === '#/definitions/number';

    const convertAttribute = (attr, name) => (isNumberAttribute(name) ? parseFloat(attr.value) : attr.value);

    const validator = validators[typeName];
    const errors = validator(attributes);
    if (errors) console.log(errors.join('\n'));

    return mapObjIndexed(convertAttribute, o.attrs);
};

/*
 * Convert <line> to <path>
 */
const lineToPath = item => {
    const { x1, y1, x2, y2 } = expectAttributes('line', item);

    item.renameElem('path');
    item.removeAttr(['x1', 'y1', 'x2', 'y2']);
    item.addAttr({
        name: 'd',
        value: `M${x1} ${y1}L${x2} ${y2}`,
        prefix: '',
        local: 'd',
    });
};

const setDAttr = (item, value) => {
    item.addAttr({
        name: 'd',
        value,
        prefix: '',
        local: 'd',
    });
};

const KAPPA = 0.5522847498307935;

const n = c => Math.floor(c * 1000) / 1000;
const m = (x, y) => `M${x} ${y}\n`;
const c = (a, b, c, d, e, f) => `C${n(a)} ${n(b)} ${n(c)} ${n(d)} ${n(e)} ${n(f)}\n`;

/*
 * Convert <circle> to <path>
 */
const circleToPath = item => {
    let { cx, cy, r } = expectAttributes('circle', item);

    cx = is(Number, cx) ? cx : parseFloat(cx);
    cy = is(Number, cy) ? cy : parseFloat(cy);
    r = is(Number, r) ? r : parseFloat(r);

    const controlDistance = r * KAPPA;

    item.renameElem('path');
    item.removeAttr(['cx', 'cy', 'r']);

    const value =
        '' +
        m(cx, cy - r) +
        c(cx + controlDistance, cy - r, cx + r, cy - controlDistance, cx + r, cy) + // 1st quadrant
        c(cx + r, cy + controlDistance, cx + controlDistance, cy + r, cx, cy + r) + // 2nd quadrant
        c(cx - controlDistance, cy + r, cx - r, cy + controlDistance, cx - r, cy) + // 3rd quadrant
        c(cx - r, cy - controlDistance, cx - controlDistance, cy - r, cx, cy - r); // 4th quadrant

    setDAttr(item, value);
};

/*
 * Convert a <polygon> command to a <path> command
 */
const polygonToPath = item => {
    const regNumber = /[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g;

    const points = item.attr('points').value.match(regNumber) || [];
    const coords = points.map(Number);

    if (coords.length < 4) return false;

    const firstPoint = coords.slice(0, 2).join(' ');
    const remainingPoints = coords.slice(2).join(' ');

    item.addAttr({
        name: 'd',
        value: `M${firstPoint} L${remainingPoints} ${item.isElem('polygon') ? 'z' : ''}`,
        prefix: '',
        local: 'd',
    });

    item.renameElem('path').removeAttr('points');
};

const rectToPath = item => {
    let { x = 0, y = 0, width, height } = expectAttributes('rect', item);

    x = is(Number, x) ? x : parseFloat(x);
    y = is(Number, y) ? x : parseFloat(y);
    width = is(Number, width) ? x : parseFloat(width);
    height = is(Number, height) ? x : parseFloat(height);

    // Values like '100%' compute to NaN, thus running after
    // cleanupNumericValues when 'px' units has already been removed.
    // TODO: Calculate sizes from % and non-px units if possible.
    if (Number.isNaN(x - y + width - height)) return;

    const pathData = `M${x} ${y} H${x + width} V${y + height} H${x} z`;

    item.addAttr({
        name: 'd',
        value: pathData,
        prefix: '',
        local: 'd',
    });

    item.renameElem('path').removeAttr(['x', 'y', 'width', 'height']);
};

const convertCommand = o => {
    // handled
    if (isLine(o)) return lineToPath(o);
    if (isCircle(o)) return circleToPath(o);
    if (isPolygon(o)) return polygonToPath(o);
    if (isRect(o)) return rectToPath(o);

    // ignored and thought about
    if (isComment(o)) return o;
    if (isPath(o)) return o;
    if (isText(o)) return o;
    if (isG(o)) return o;

    // ignored and not yet thought about
    if (isEllipse(o)) return o;
    if (isPolyline(o)) return o;
    if (isStop(o)) return o;
    if (isUse(o)) return o;
    if (isFontFace(o)) return o;
    if (isMissingGlyph(o)) return o;
    if (isGlyph(o)) return o;
    if (isImage(o)) return o;
    if (isFeColorMatrix(o)) return o;

    // throw new Error(`Don't understand SVG element: ${ JSON.stringify(o) }: `)
};

/**
 * Converts basic shape to more compact path.
 * It also allows further optimizations like
 * combining paths with similar attributes.
 *
 * @see http://www.w3.org/TR/SVG/shapes.html
 *
 * @param {Object} item current iteration item
 * @param {Object} params plugin params
 * @return {Boolean} if false, item will be filtered out
 *
 * @author Lev Solntsev
 */
const fn = (item, params) => convertCommand(item);

export default convertCommand;

const convertShapeToPath = {
    type: 'perItem',
    active: true,
    description: 'Another shape converter',
    params: { convertArcs: true },
    fn,
};

export { convertShapeToPath };
